import { useEffect, useRef, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import { SurveyLayout } from "./SurveyLayout";
import { surveyFormConfig } from "./surveyFormConfig";
import { usePanel } from "hooks/usePanel";
import { GenericLoading } from "components/ui/Loader";
import { SurveyFormValues } from "./SurveyFormValues";
import { OperationResponse } from "api/survey";
import { surveyAPI } from "services/swagger-api/surveyApi";

interface SurveyFormProps {
  surveyId?: string;
  onSuccess?: () => void;
  onSubmit?: (values: SurveyFormValues, surveyId?: string) => Promise<OperationResponse | undefined>;
}

export const SurveyForm: React.FC<SurveyFormProps> = ({
  surveyId,
  onSubmit,
  onSuccess,
}) => {
  const formikRef = useRef<FormikProps<SurveyFormValues>>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(!!surveyId);
  const [initialValues, setInitialValues] = useState<SurveyFormValues>(surveyFormConfig.initialValues);
  const { setPanelActions, closePanel } = usePanel();

  const isEditMode = !!surveyId;

  // Charger les données du questionnaire si en mode édition
  useEffect(() => {
    const loadSurvey = async () => {
      if (surveyId) {
        try {
          setIsLoading(true);
          const response = await surveyAPI.getSurvey({ id: surveyId });
          const survey = response.survey;
          if (survey) {
            setInitialValues({
              name: survey.name || '',
              code: '',  // code is not returned by API
              description: survey.description || '',
              contextId: survey.contextId || '',
              branchId: survey.branchId || '',
              isActive: survey.isActive ?? true
            });
          }
        } catch (error) {
          console.error('Error loading survey:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadSurvey();
  }, [surveyId]);

  useEffect(() => {
    if (formikRef.current) {
      setPanelActions({
        closeLabel: "COMMON.CANCEL",
        submitLabel: isEditMode ? "COMMON.SAVE" : "COMMON.CREATE",
        submitDisabled: !isFormValid || isLoading,
        onSubmit: () => {
          formikRef.current?.submitForm();
        },
      });
    }
  }, [formikRef, setPanelActions, isFormValid, isLoading, isEditMode]);

  const handleSubmit = async (values: SurveyFormValues) => {
    setIsLoading(true);
    if (onSubmit) {
      try {
        const response = await onSubmit(values, surveyId);
        if (response?.isSuccess) {
          onSuccess?.();
          closePanel();
        }
      } catch (error) {
        console.error('Error on submit survey:', error);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    setIsLoading(false);
  };

  if (isLoading && isEditMode) {
    return <GenericLoading isPageCentered loading />;
  }

  return (
    <div className="relative">
      {isLoading && <GenericLoading loading isParentSized />}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={surveyFormConfig.validationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {({ isValid }) => {
          if (isValid !== isFormValid) {
            setIsFormValid(isValid);
          }
          return (
            <Form>
              <SurveyLayout isEditMode={isEditMode} />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
